// https://github.com/js-cookie/js-cookie

/*
 * User credentials and preferences, facilitates registration and login.
 * Cookie and tenancy settings are also stored here.
 */

import Cookies from 'js-cookie'
import Crypto from 'crypto-js'

// Used by tryAutoLoginFromPersisted.
// Small helper so cookie flags are applied consistently.
function cookieOpts(days = 30) {
  // Use secure cookies only when running over HTTPS.
  const secure =
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:'

  return {
    expires: days,
    sameSite: 'Lax',
    path: '/',
    secure,
  }
}

const getDefaultState = () => ({
  user: null,
  auth_token: null,
  logged_in_time: null,
})

export const state = () => getDefaultState()

export const mutations = {
  RESET_STATE(state) {
    Object.assign(state, getDefaultState())
  },

  set_token(state, token) {
    state.auth_token = token
  },

  set_logged_in_time(state, date) {
    state.logged_in_time = date
  },

  set_user(state, data) {
    state.user = data.user
  },
}

export const actions = {
  // -------------------------------------------------------------------------
  // Registration
  // -------------------------------------------------------------------------

  async register_user({ dispatch }, payload) {
    // Clear any old session quietly.
    await dispatch('logoutUser', {
      invisible: true,
    })

    const url =
      this.$config.APP_ENV === 'development'
        ? `${this.$config.BASE_API}/auth/register_test_user`
        : `${this.$config.BASE_API}/auth/register_user`

    const body = {
      batch_id: payload.batch_id,
      domain: payload.domain,
      enable_ga:
      Cookies.get('allow-analytics') === '1'
        ? 1
        : 0,
    
    remember_me:
      Cookies.get('remember-me') === '1'
        ? 1
        : 0,
    }

    if (payload.recaptcha_token) {
      body.recaptcha_token = payload.recaptcha_token
    } else {
      body.key = payload.key
      body.captcha = payload.captcha
    }

    const response = await this.$axios.$post(url, body)

    if (response.status === false) {
      return Promise.reject(response)
    }

    await dispatch('apply_user', response)

    // Safe analytics event.
    try {
      if (this.$ga) {
        this.$ga.event(
          'User Register',
          'register',
          response.user.batch_id,
          response.user.user_uuid
        )
      }
    } catch (_) {}

    return response
  },

  // -------------------------------------------------------------------------
  // Login
  // -------------------------------------------------------------------------

  login_user({ dispatch }, data) {
    return new Promise((resolve, reject) => {
      this.$axios
        .$post(
          this.$config.BASE_API + '/auth/login_user',
          {
            password: data.password,
          }
        )
        .then(async (response) => {
          try {
            // 1) Attach the token first so following calls are authenticated.
            await dispatch('apply_user', response)

            // 2) Persist cookie preferences.
            // Preference failures should not block login.
            try {
              await dispatch(
                'setRememberMePreference',
                response.remember_me
              )

              await dispatch(
                'setGAPreference',
                response.enable_ga
              )
            } catch (_) {}

            try {
              if (this.$ga) {
                this.$ga.event(
                  'User Login',
                  'register',
                  response.user.batch_id,
                  response.user.user_uuid
                )
              }
            } catch (_) {}

            // 3) Restore profile answers, scores and history.
            try {
              await dispatch(
                'profile/recallProfile',
                response.user,
                {
                  root: true,
                }
              )
            } catch (_) {}

            // 4) Build dashboard tiles after the profile is restored.
            try {
              await dispatch(
                'navigation/processMenuItems',
                null,
                {
                  root: true,
                }
              )
            } catch (_) {}

            // 5) Land on the dashboard.
            const stayOnDashboard =
            data &&
            data.stayOnDashboard === true
          
          if (
            !stayOnDashboard &&
            this.$router.currentRoute.path !==
              '/dashboard'
          ) {
            await this.$router.push({
              path: '/dashboard',
            })
          }

            resolve(response)
          } catch (error) {
            reject(error)
          }
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  // Called by registration, login and automatic resume.
  async apply_user({ commit, dispatch }, response) {
    commit('set_token', response.token)
    commit('set_logged_in_time', new Date())
    commit('set_user', response)

    if (
      this.$axios &&
      typeof this.$axios.setToken === 'function'
    ) {
      this.$axios.setToken(
        response.token,
        'Bearer'
      )
    } else if (this.$axios) {
      this.$axios.setHeader(
        'Authorization',
        `Bearer ${response.token}`
      )
    }

    // Set content/distribution for this user.
    await dispatch(
      'processors/setDistributionSettings',
      {
        tree: response.tree,
        batch: response.batch_id,
      },
      {
        root: true,
      }
    )

    // Rebuild dashboard tiles after content exists.
    await dispatch(
      'navigation/processMenuItems',
      null,
      {
        root: true,
      }
    )
  },

    /**
   * Silently restore a remembered session using encrypted persisted state.
   *
   * A user object or remembered password alone does not mean the user is
   * authenticated. A valid live session requires both:
   *
   * - state.user
   * - state.auth_token
   *
   * The caller may pass stayOnDashboard so login_user does not perform its
   * own route change while a navigation guard is resolving.
   */
    async tryAutoLoginFromPersisted(
      {
        state,
        dispatch,
        getters,
      },
      {
        stayOnDashboard = true,
      } = {}
    ) {
      /*
       * A complete authenticated session already exists.
       */
      if (
        state.user &&
        state.auth_token
      ) {
        if (
          this.$axios &&
          typeof this.$axios.setToken ===
            'function'
        ) {
          this.$axios.setToken(
            state.auth_token,
            'Bearer'
          )
        } else if (this.$axios) {
          this.$axios.setHeader(
            'Authorization',
            `Bearer ${state.auth_token}`
          )
        }
  
        return true
      }
  
      /*
       * Remember Me must still be enabled.
       */
      if (
        getters.getRememberMeCookieStatus !==
        1
      ) {
        return false
      }
  
      if (
        typeof window ===
        'undefined'
      ) {
        return false
      }
  
      try {
        const raw =
          window.localStorage.getItem(
            'vuex'
          )
  
        if (!raw) {
          return false
        }
  
        const encryptionKey =
          Cookies.get('myuse')
  
        if (!encryptionKey) {
          return false
        }
  
        const bytes =
          Crypto.AES.decrypt(
            raw,
            encryptionKey
          )
  
        const decryptedText =
          bytes.toString(
            Crypto.enc.Utf8
          )
  
        if (!decryptedText) {
          return false
        }
  
        const decrypted =
          JSON.parse(
            decryptedText
          )
  
        const password =
          decrypted &&
          decrypted.auth &&
          decrypted.auth.user &&
          decrypted.auth.user.password
  
        if (!password) {
          return false
        }
  
        /*
         * Perform a real login so the token, profile, processor tree,
         * navigation history and optional modules are refreshed.
         *
         * Do not let login_user redirect while a route guard or Welcome Back
         * dialog is deciding where the user should be sent.
         */
        await dispatch(
          'login_user',
          {
            password,
            stayOnDashboard,
          }
        )
  
        return Boolean(
          state.user &&
          state.auth_token
        )
      } catch (error) {
        console.error(
          'Could not restore remembered MyUSE session:',
          error
        )
  
        return false
      }
    },

       /**
     * Log out the current user.
     *
     * There are two different uses of this action:
     *
     * EXPLICIT LOGOUT
     * ----------------
     * The user intentionally presses Logout.
     *
     * In that case we must:
     *
     * - revoke the API session;
     * - disable Remember Me;
     * - remove the persisted-state encryption key;
     * - clear Vuex/local storage;
     * - return to the landing page.
     *
     *
     * INVISIBLE LOGOUT
     * ----------------
     * Used internally before registering a new user.
     *
     * In that case we must clear an old authenticated session,
     * but MUST NOT destroy the user's currently selected:
     *
     * - Remember Me preference;
     * - MyUSE encryption-key cookie.
     *
     * Otherwise registration reads remember_me = 0 even when the
     * user explicitly selected Remember Me on the welcome page.
     */
       async logoutUser(
        {
          state,
          dispatch,
        },
        {
          invisible = false,
        } = {}
      ) {
        const currentUser =
          state.user
  
        /*
         * Revoke the current API session while the bearer token
         * is still available.
         *
         * Failure here must not prevent local cleanup.
         */
        if (currentUser) {
          try {
            await this.$axios.$post(
              this.$config.BASE_API +
                '/auth/logout_user'
            )
  
            try {
              if (this.$ga) {
                this.$ga.event(
                  'User Logout',
                  'logout'
                )
              }
            } catch (_) {}
          } catch (_) {
            /*
             * The token may already have expired, or the network
             * may be unavailable. Local cleanup must still work.
             */
          }
        }
  
        /*
         * IMPORTANT:
         *
         * Only an EXPLICIT logout disables Remember Me.
         *
         * register_user() deliberately calls:
         *
         *   logoutUser({ invisible: true })
         *
         * before creating the new user. In that situation the
         * Remember Me switch selected on index.vue must survive.
         */
        if (!invisible) {
          Cookies.set(
            'remember-me',
            '0',
            cookieOpts()
          )
  
          /*
           * Remove the encryption-key cookie only when the user
           * explicitly logs out.
           *
           * Preserve it during invisible registration cleanup so
           * the newly registered remembered session can continue
           * using encrypted Vuex persistence.
           */
          try {
            Cookies.remove(
              'myuse',
              {
                path: '/',
              }
            )
  
            Cookies.remove(
              'myuse',
              {
                path: '/',
                domain: '.myuse.ie',
              }
            )
          } catch (_) {}
        }
  
        /*
         * Remove the bearer token before resetting Vuex.
         */
        if (
          this.$axios &&
          typeof this.$axios.setToken ===
            'function'
        ) {
          this.$axios.setToken(
            false
          )
        } else if (
          this.$axios &&
          typeof this.$axios.setHeader ===
            'function'
        ) {
          this.$axios.setHeader(
            'Authorization',
            false
          )
        }
  
        /*
         * Clear the old live session.
         *
         * During registration this deliberately clears the OLD
         * user's state while leaving Remember Me preference intact.
         */
        await dispatch(
          'clearAllVuex'
        )
  
        /*
         * Invisible cleanup ends here.
         *
         * Do not change cookies.
         * Do not navigate.
         * Do not display a logout notification.
         */
        if (invisible) {
          return
        }
  
        /*
         * Explicit logout returns to the welcome page.
         */
        if (
          this.$router.currentRoute.path !==
            '/'
        ) {
          try {
            await this.$router.replace({
              path: '/',
            })
          } catch (error) {
            const duplicated =
              error &&
              (
                error.name ===
                  'NavigationDuplicated' ||
                String(
                  error.message || ''
                ).includes(
                  'Avoided redundant navigation'
                )
              )
  
            if (!duplicated) {
              throw error
            }
          }
        }
  
        /*
         * Explicit logout confirmation.
         */
        setTimeout(() => {
          if (
            this._vm &&
            this._vm.$dialog
          ) {
            this._vm.$dialog.message.info(
              'You have been logged out.',
              {
                position: 'top',
                color: 'primary',
                timeout: 4000,
              }
            )
          }
        }, 600)
      },
    /**
   * Clear all live and persisted MyUSE session state.
   *
   * This method deliberately does not remove general consent cookies such as
   * allow-analytics. It removes only authentication/session material.
   */
    async clearAllVuex({
      dispatch,
      commit,
    }) {
      await dispatch(
        'profile/resetState',
        '',
        {
          root: true,
        }
      )
  
      await dispatch(
        'navigation/resetState',
        '',
        {
          root: true,
        }
      )
  
      await dispatch(
        'feedback/resetState',
        '',
        {
          root: true,
        }
      )
  
      commit(
        'RESET_STATE'
      )
  
      if (
        typeof window !==
        'undefined'
      ) {
        try {
          window.localStorage.removeItem(
            'vuex'
          )
        } catch (_) {}
  
        /*
         * Remove any older session-storage copies created by previous builds.
         */
        try {
          window.sessionStorage.removeItem(
            'vuex'
          )
        } catch (_) {}
      }
  
      /*
       * Ensure Axios cannot reuse a bearer token after state reset.
       */
      if (
        this.$axios &&
        typeof this.$axios.setToken ===
          'function'
      ) {
        this.$axios.setToken(
          false
        )
      } else if (
        this.$axios &&
        typeof this.$axios.setHeader ===
          'function'
      ) {
        this.$axios.setHeader(
          'Authorization',
          false
        )
      }
    },

  // -------------------------------------------------------------------------
  // Preferences
  // -------------------------------------------------------------------------

  setGAPreference({ dispatch }, preference) {
    // Use stable cookie values: "1" or "0".
    const normalized =
      preference === 1 ||
      preference === '1' ||
      preference === true ||
      preference === 'true'
        ? '1'
        : '0'

    Cookies.set(
      'allow-analytics',
      normalized,
      cookieOpts()
    )

    dispatch('changeGAState', normalized)

    return dispatch('storeCookiePrefs')
  },

  setRememberMePreference(
    { dispatch },
    preference
  ) {
    const normalized =
      preference === 1 ||
      preference === '1' ||
      preference === true ||
      preference === 'true'
        ? '1'
        : '0'

    Cookies.set(
      'remember-me',
      normalized,
      cookieOpts()
    )

    return dispatch('storeCookiePrefs')
  },

  // This protected route is mounted under /activity in api.php.
  storeCookiePrefs({ state }) {
    if (!state.user) {
      return Promise.resolve()
    }

    const enableGA =
      Cookies.get('allow-analytics') === '1'
        ? 1
        : 0

    const rememberMe =
      Cookies.get('remember-me') === '1'
        ? 1
        : 0

    return this.$axios
      .$post(
        this.$config.BASE_API +
          '/activity/set_cookie_settings',
        {
          enable_ga: enableGA,
          remember_me: rememberMe,
        }
      )
      .catch(() => {})
  },

  async changeGAState({ state }, preference) {
    const ga = this.$ga

    const enabled =
      preference === 1 ||
      preference === '1' ||
      preference === true ||
      preference === 'true'

    if (enabled && state.user) {
      try {
        if (
          ga &&
          typeof ga.enable === 'function'
        ) {
          ga.enable()
        }

        if (ga && typeof ga.set === 'function') {
          await ga.set(
            'dimension1',
            state.user.batch_id
          )

          if (state.user.tenant_id) {
            await ga.set(
              'dimension2',
              state.user.tenant_id
            )
          }

          await ga.set(
            'dimension3',
            state.user.user_uuid
          )
        }
      } catch (_) {}
    } else {
      try {
        if (
          ga &&
          typeof ga.disable === 'function'
        ) {
          ga.disable()
        }
      } catch (_) {}

      // Keep cookie cleanup limited to existing analytics cookies.
      try {
        if (
          typeof window !== 'undefined' &&
          window.ga
        ) {
          window.ga('remove')
        }

        const domains = [
          'localhost',
          '.myuse.ie',
          '.myuse-test.ie',
        ]

        const cookieNames = [
          '_ga',
          '_gid',
          '_gat',
        ]

        cookieNames.forEach((name) => {
          // Remove host-only versions.
          try {
            Cookies.remove(name, {
              path: '/',
            })
          } catch (_) {}

          // Remove domain-specific versions.
          domains.forEach((domain) => {
            try {
              Cookies.remove(name, {
                path: '/',
                domain,
              })
            } catch (_) {}
          })
        })
      } catch (_) {}
    }
  },
}

export const getters = {
  getUser(state) {
    return state.user
  },

  getUserPassword(state) {
    return state.user && state.user.password
      ? state.user.password
      : null
  },

  getToken(state) {
    return state.auth_token
  },

  getRememberMeState() {
    return null
  },

  getGAState() {
    return null
  },

  getLastLogin(state) {
    return state.logged_in_time
  },

  getRememberMeCookieStatus() {
    return parseInt(
      Cookies.get('remember-me') || '0',
      10
    )
  },

  getGACookieStatus() {
    return parseInt(
      Cookies.get('allow-analytics') || '0',
      10
    )
  },
}